import React, { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { getDaysAgoEastern } from '../../lib/dateHelpers'

function fmtTimeShort(iso) {
  if (!iso) return '--'
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
}

export default function TimeEditModal({ record, field, onSave, onClose }) {
  var { user, profile } = useAuth()
  var isOwner = profile?.role === 'owner'
  var fieldLabel = field === 'clock_in' ? 'Clock In' : 'Clock Out'
  var currentVal = record[field]
  var currentTimeStr = currentVal ? new Date(currentVal).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/New_York' }) : ''
  var [timeVal, setTimeVal] = useState(currentTimeStr)
  var [reason, setReason] = useState('')
  var [saving, setSaving] = useState(false)
  var [error, setError] = useState(null)

  // Check 3-day limit for non-owners
  var threeDaysAgo = getDaysAgoEastern(3)
  if (!isOwner && record.work_date < threeDaysAgo) {
    return (
      <div style={st.overlay} onClick={onClose}>
        <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
          <div style={st.title}>CANNOT EDIT</div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '16px' }}>
            Records older than 3 days can only be edited by the owner.
          </div>
          <button onClick={onClose} style={st.cancelBtn}>CLOSE</button>
        </div>
      </div>
    )
  }

  var handleSave = async function() {
    if (!reason.trim()) { setError('Reason is required'); return }
    if (!timeVal) { setError('Enter a time'); return }
    setSaving(true)
    setError(null)
    try {
      var updates = {}
      // Build full timestamp: keep work_date, change time, Eastern timezone (UTC-4 for EDT)
      var newTs = new Date(record.work_date + 'T' + timeVal + ':00-04:00')
      if (isNaN(newTs.getTime())) { setError('Invalid time'); setSaving(false); return }
      updates[field] = newTs.toISOString()
      if (field === 'clock_out') updates.status = 'clocked_out'

      // Preserve originals on first edit only
      if (!record['original_' + field] && record[field]) {
        updates['original_' + field] = record[field]
      }
      updates.edited_at = new Date().toISOString()
      updates.edited_by = user.id
      updates.edit_reason = reason.trim()

      // Do NOT include total_hours — it's GENERATED ALWAYS
      var { error: dbErr } = await supabase.from('daily_attendance').update(updates).eq('id', record.id)
      if (dbErr) { setError(dbErr.message); setSaving(false); return }
      onSave()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={st.title}>EDIT {fieldLabel.toUpperCase()}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' }}>X</button>
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '16px' }}>
          {new Date(record.work_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          {record['original_' + field] && <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.25)' }}>{' \u2014 Originally: '}{fmtTimeShort(record['original_' + field])}</span>}
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={st.label}>{fieldLabel.toUpperCase()} TIME (EASTERN)</label>
          <input type="time" value={timeVal} onChange={function(e) { setTimeVal(e.target.value) }} style={st.input} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={st.label}>REASON FOR CHANGE *</label>
          <input value={reason} onChange={function(e) { setReason(e.target.value) }} style={st.input} placeholder="e.g. Forgot to clock in, arrived at 7:30" />
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onClose} style={st.cancelBtn}>CANCEL</button>
          <button onClick={handleSave} disabled={saving} style={{ ...st.saveBtn, opacity: saving ? 0.5 : 1 }}>{saving ? 'SAVING...' : 'SAVE'}</button>
        </div>
      </div>
    </div>
  )
}

var st = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '24px', width: '100%', maxWidth: '400px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#ffffff' },
  label: { display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#fff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
  cancelBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  saveBtn: { flex: 1, padding: '10px 20px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
}
